import React, { useEffect, useState } from 'react';
import { Avatar, Button, Icon, IconButton, Menu, MenuItem, StatusTag } from '~/UIKit';
import { ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText } from '@mui/material';
import { IFitoListItem } from '../../models';
import { MoreVert } from '@mui/icons-material';
import { FitoListItemStyles } from './FitoListItemStyles';
import { useFitoListItem } from './useFitoListItem';
import { FitoListItemMainAction } from './FitoListItemMainAction';
import { useDateFns } from '~/Hooks';
import { AttendanceStatus, AttendanceStatusType } from '~/Models/DTO/Attendance.model';

interface IFitoListItemProps {
  item: IFitoListItem;
  index: number;
  listName?: string;
  isBeingRearranged?: boolean;
  isAnyItemSelected?: boolean;
  moveItem?: (id: string, hoverIndex: number) => void;
  findItemIndex?: (id: string) => number;
  onSelect?: (id: string, checked?: boolean) => void;
  multiSelect?: boolean;
  isSelectPossible?: boolean;
  isSelectionActive?: boolean;
}

export const FitoListItem = ({
  item,
  listName,
  isBeingRearranged,
  index,
  moveItem,
  findItemIndex,
  onSelect,
  isAnyItemSelected,
  multiSelect = false,
  isSelectPossible = true,
  isSelectionActive = false,
}: IFitoListItemProps) => {
  const { classes } = FitoListItemStyles({
    isBeingRearranged: !!isBeingRearranged,
    status: !!item.status,
    hasCheckBox: !!item.checkable,
    active: !!item.active,
  })();
  const [checked, setChecked] = useState(!!item.checked);
  const { getHoursAndMinutes, formatDate } = useDateFns();

  const {
    drag,
    drop,
    handlerId,
    anchorEl,
    handleMenuOpen,
    handleClose,
    onCheckboxChecked,
  } = useFitoListItem({
    item,
    listName,
    index,
    moveItem,
    findItemIndex,
    onSelect,
    setChecked,
  });

  const {
    checkable,
    disabled,
    header,
    notificationIcon,
    onClick,
    picture,
    subheader,
    actions,
    status,
    actionButton,
    actionButtonHandler,
    dateTimeStatus,
    selectComponent,
  } = item;

  useEffect(() => {
    if (item.checked !== checked) {
      setChecked(!!item.checked);
    }
  }, [item.checked, setChecked]);

  const statusTag = status ? (
    <>
      {typeof status === 'object' ? (
        <StatusTag key={item.id} status={status?.color} size="small">
          {status?.label}
        </StatusTag>
      ) : (
        <StatusTag key={item.id} status={status} size="small">
          {status}
        </StatusTag>
      )}
    </>
  ) : null;

  const getTime = (date: string | Date | undefined) => {
    const { hour, minute } = date ? getHoursAndMinutes(date) : { hour: '', minute: '' };
    return `${hour}:${minute}`;
  };

  const dateTimeStatusSection =
    dateTimeStatus?.date !== undefined ? (
      <div>
        {dateTimeStatus?.status !== undefined ? (
          dateTimeStatus?.status ? (
            <StatusTag status={AttendanceStatusType[dateTimeStatus.status]} size="small">
              {AttendanceStatus[dateTimeStatus.status]}
            </StatusTag>
          ) : (
            <StatusTag status={AttendanceStatusType.Pending}>{AttendanceStatus.Pending}</StatusTag>
          )
        ) : null}
        <div className={classes.dateWrapper}>
          <span>{formatDate(dateTimeStatus.date as string, 'dd MMM yyyy')},</span>
          <span>{getTime(dateTimeStatus.date)}</span>
        </div>
      </div>
    ) : null;

  const onListItemClick = (e: any) => {
    if (isBeingRearranged) {
      return;
    }
    if (isSelectionActive) {
      onCheckboxChecked(e, !checked);
      return;
    }
    if (!onClick && (multiSelect || isSelectPossible)) {
      onCheckboxChecked(e, !checked);
      return;
    }
    onClick?.();
  };

  return (
    <div
      className={classes.listItemContainer}
      ref={(node) => (isBeingRearranged ? drag(drop(node)) : drag(drop(null)))}
      data-handler-id={handlerId}
      role="Handle"
      data-testid="FitoListItem"
    >
      <ListItem classes={{ container: classes.listItem }} onClick={onListItemClick} button>
        {checkable && (
          <ListItemSecondaryAction className={classes.secondaryListItemAction}>
            <FitoListItemMainAction
              index={index}
              listName={listName || 'list'}
              checkable={checkable}
              checked={checked}
              onCheckboxChecked={onCheckboxChecked}
              disabled={disabled}
              isBeingRearranged={isBeingRearranged}
              multiSelect={multiSelect}
              hasAPicture={!!item.picture}
            />
          </ListItemSecondaryAction>
        )}
        {!!selectComponent && selectComponent}
        {picture && (
          <ListItemAvatar classes={{ root: classes.ListItemAvatar }}>
            {typeof picture === 'string' ? (
              <Avatar size={30} alt={picture} src={picture} name={header} />
            ) : (
              <Avatar size={30} name={header}>
                {picture}
              </Avatar>
            )}
          </ListItemAvatar>
        )}
        <ListItemText
          className={classes.listItemText}
          primary={header}
          secondary={subheader}
          id={`${listName}-${index}`}
        />
        {dateTimeStatusSection}
        {statusTag}
        {item.customElement}
        {actionButton && actionButtonHandler && (
          <Button variant="outlined" size="small" onClick={actionButtonHandler}>
            {actionButton}
          </Button>
        )}
        {notificationIcon && (
          <ListItemSecondaryAction className={classes.listItemSecondaryAction}>
            <Icon>{notificationIcon}</Icon>
          </ListItemSecondaryAction>
        )}

        {!!actions?.length && !isAnyItemSelected && (
          <ListItemSecondaryAction
            data-testid="FitoListItemSecondaryAction"
            className={classes.listItemSecondaryAction}
          >
            <IconButton
              aria-controls="list-actions-menu"
              aria-haspopup
              onClick={handleMenuOpen}
              className={classes.listItemMenuButton}
              size="large"
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id="list-actions-menu"
              keepMounted
              onClose={handleClose}
              open={Boolean(anchorEl)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {actions.map((action, index) => (
                <MenuItem
                  key={`${listName}_MENU_ITEM_${index}`}
                  onClick={() => {
                    action.onClick(item);
                    handleClose();
                  }}
                  classes={{ root: classes.menuItem }}
                >
                  {action.name}
                </MenuItem>
              ))}
            </Menu>
          </ListItemSecondaryAction>
        )}
      </ListItem>
    </div>
  );
};
